import React, { useEffect, useState } from 'react';
import { Button, Pager, SearchBar } from '@keplerco/core';
import { useAppActions, useAppState } from '../../../overmind';
import { transformScore } from '../../../pages/analytics/ranked-entities/ranked-entities.helpers';
import { DataPoint } from '../../../components/charts/recharts.models';
import { RankedPerformanceChart } from '../../../components/charts/ranked-performance-chart/ranked-performance-chart';
import { AssessmentSearchParams, FilteringSearchParams, PagingSearchParams, PermissionSearchParams, SortingSearchParamsDeprecated, SearchingSearchParams } from '../../../models/overmind/search-params';
import { EmptyState } from '../../../components/general/empty-state/empty-state';
import { AnalyticsFilters } from '../../../pages/analytics/analytics-filters/analytics-filters';
import classNames from 'classnames';
import { SkeletonOverlay } from '../../../components/general/loading-state/skeleton-overlay';
import { IAssessmentScoreSkillWidgetProps } from './assessment-score-skill.models';
import { WidgetCard } from '../../../pages/analytics/analytics-filters/widget-card';
import { DownloadIcon } from '../../../design/icons/download.icon';
import { FiltersOutlet } from '../../../pages/analytics/analytics-filters/filters-outlet';
import { SelectedFilterOption } from '../../../pages/analytics/analytics-filters/analytics-filters.models';
import { getSelectedFilters, removeFilterOption } from '../../../pages/analytics/analytics-filters/analytics-filters.helpers';
import { DEFAULT_FILTERING_SEARCH_PARAMS } from '../../../library/consts/consts';

export default function AssessmentScoreSkillWidget({ baseSearchParams, filtersOptions }: IAssessmentScoreSkillWidgetProps) {
  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useState<SortingSearchParamsDeprecated & PagingSearchParams & SearchingSearchParams>({
    sortBy: undefined,
    sortAscending: true,
    page: 1,
    pageSize: 10,
    search: undefined,
  });
  const [filteringSearchParams, setFilteringSearchParams] = useState<FilteringSearchParams>(DEFAULT_FILTERING_SEARCH_PARAMS);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilterOption[]>([]);
  const [dataPoints, setDataPoints] = useState<DataPoint[]>([]);
  const [totalPages, setTotalPages] = useState(1);

  async function getData(request: AssessmentSearchParams & PermissionSearchParams & FilteringSearchParams & SortingSearchParamsDeprecated & PagingSearchParams & SearchingSearchParams) {
    const response = await actions.getAssessmentAverageScores(request);
    setDataPoints(transformScore(response?.averages ?? [], companyVariables));
    setTotalPages(response?.totalPages ?? 1);
  }

  useEffect(() => {
    async function initialise() {
      setIsLoading(true);
      await getData({ ...baseSearchParams, ...searchParams, ...filteringSearchParams });
      setIsLoading(false);
    }

    initialise();
  }, [baseSearchParams]);

  return (
    <SkeletonOverlay isLoading={isLoading} width="100%">
      <WidgetCard
        title={!!baseSearchParams.learnerSlug ? 'Assessment score per skill' : 'Average score per skill'}
        subtitle={!!baseSearchParams.learnerSlug ? 'The  score of each skill included in this assessment.' : 'The average score of each skill included in this assessment across all participants.'}
        showSkillLevelHelp
      >
        <div>
          <div style={{ display: 'flex', flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
            <SearchBar
              label="Search"
              loading={isSearching}
              onInput={async event => {
                setIsSearching(true);
                const search = (event.target as HTMLInputElement).value;
                setSearchParams({ ...searchParams, search, page: 1 });
                await getData({ ...baseSearchParams, ...searchParams, ...filteringSearchParams, search, page: 1 });
                setIsSearching(false);
              }}
            />

            {!baseSearchParams.learnerSlug && (
              <AnalyticsFilters
                departmentOptions={filtersOptions?.departmentOptions}
                teamOptions={filtersOptions?.teamOptions}
                roleOptions={filtersOptions?.roleOptions}
                skillOptions={filtersOptions?.skillOptions}
                countryOptions={filtersOptions?.countryOptions}
                filteringSearchParams={filteringSearchParams}
                setFilteringSearchParams={setFilteringSearchParams}
                applyFilters={async () => {
                  setIsLoading(true);
                  const selectedFilters = getSelectedFilters({
                    searchParams: filteringSearchParams,
                    departmentOptions: filtersOptions?.departmentOptions,
                    teamOptions: filtersOptions?.teamOptions,
                    roleOptions: filtersOptions?.roleOptions,
                    skillOptions: filtersOptions?.skillOptions,
                    countryOptions: filtersOptions?.countryOptions,
                  });
                  setSelectedFilters(selectedFilters);
                  await getData({ ...baseSearchParams, ...searchParams, ...filteringSearchParams });
                  setIsLoading(false);
                }}
                clearFilters={async () => {
                  if (!filteringSearchParams.departmentSlugs && !filteringSearchParams.teamSlugs && !filteringSearchParams.roleSlugs && !filteringSearchParams.skillSlugs && !filteringSearchParams.countryIds) return;
                  setIsLoading(true);
                  setFilteringSearchParams(DEFAULT_FILTERING_SEARCH_PARAMS);
                  setSelectedFilters([]);
                  await getData({ ...baseSearchParams, ...searchParams, ...DEFAULT_FILTERING_SEARCH_PARAMS });
                  setIsLoading(false);
                }}
              />
            )}

            <div style={{ marginLeft: 'auto' }}>
              <Button
                square
                theme="dark"
                type="button"
                isLoading={isDownloading}
                loaderText="Downloading..."
                onClick={async () => {
                  setIsDownloading(true);
                  await actions.getAssessmentAverageScoresCSV({ ...baseSearchParams, ...searchParams, ...filteringSearchParams });
                  setIsDownloading(false);
                }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <DownloadIcon />
                  CSV
                </div>
              </Button>
            </div>
          </div>

          <FiltersOutlet
            selectedFilters={selectedFilters}
            removeFilter={async filter => {
              setIsLoading(true);
              const updatedFilteringParams = removeFilterOption(filteringSearchParams, filter);
              setFilteringSearchParams(updatedFilteringParams);
              setSelectedFilters(prevSelectedFilters => prevSelectedFilters.filter(option => option.slug !== filter.slug));
              await getData({ ...baseSearchParams, ...searchParams, ...updatedFilteringParams });
              setIsLoading(false);
            }}
          />
        </div>

        {!dataPoints.length ? (
          <EmptyState title="Hang tight!" subtitle="We are waiting for data." height="365px" />
        ) : (
          <React.Fragment>
            <RankedPerformanceChart dataPoints={dataPoints} fill="baby-blue" />

            <div className={classNames({ invisible: !totalPages || totalPages < 2 })}>
              <Pager
                activePageNumber={searchParams.page ?? 1}
                pageCount={totalPages}
                onPageChange={async page => {
                  if (page === searchParams.page) return;
                  setIsLoading(true);
                  setSearchParams({ ...searchParams, page });
                  await getData({ ...baseSearchParams, ...searchParams, ...filteringSearchParams, page });
                  setIsLoading(false);
                }}
              />
            </div>
          </React.Fragment>
        )}
      </WidgetCard>
    </SkeletonOverlay>
  );
}
